/**
 * StartupBannerTab.jsx
 * Admin panel to configure the in-app startup banner (announcement, promo, seasonal wishes).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Megaphone, Save, Calendar, Link as LinkIcon, Smile, Clock, Eye, Palette } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT = {
  enabled: false,
  title: '',
  message: '',
  emoji: '🎉',
  image_url: '',
  duration_seconds: 4,
  skippable: true,
  link_url: '',
  link_label: 'Jetzt ansehen',
  background_color: '#0B0B0F',
  primary_color: '#EAB308',
  schedule_mode: 'always',
  start_datetime: '',
  end_datetime: '',
  campaign_id: '',
};

const toInputDateTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
      d.getMinutes()
    )}`;
  } catch {
    return '';
  }
};

const fromInputDateTime = (local) => {
  if (!local) return '';
  try {
    return new Date(local).toISOString();
  } catch {
    return '';
  }
};

const StartupBannerTab = ({ token }) => {
  const [banner, setBanner] = useState(DEFAULT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/mobile-app/startup-banner`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setBanner({ ...DEFAULT, ...res.data });
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const update = (key, value) => setBanner((b) => ({ ...b, [key]: value }));

  const save = async () => {
    // Validate
    if (banner.enabled && !banner.title.trim() && !banner.message.trim()) {
      toast.error('Titel oder Nachricht erforderlich');
      return;
    }
    if (banner.schedule_mode === 'scheduled') {
      if (banner.start_datetime && banner.end_datetime) {
        if (new Date(banner.end_datetime) <= new Date(banner.start_datetime)) {
          toast.error('Enddatum muss nach Startdatum liegen');
          return;
        }
      }
    }

    setSaving(true);
    try {
      const payload = {
        ...banner,
        duration_seconds: Number(banner.duration_seconds) || 4,
      };
      await axios.put(`${API}/admin/mobile-app/startup-banner`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Startup-Banner gespeichert');
    } catch (e) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-gray-500">Lade Banner-Einstellungen…</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-rose-500/20 rounded-xl">
              <Megaphone className="w-6 h-6 text-rose-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Startup-Banner</h3>
              <p className="text-sm text-gray-500 max-w-xl">
                Fullscreen-Banner beim App-Start (z.B. Neujahrsgrüsse, Promo, neue Features). Wird bei jedem App-Start
                angezeigt, solange er aktiv ist.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-900">Aktiv</span>
            <Switch
              checked={!!banner.enabled}
              onCheckedChange={(v) => update('enabled', v)}
              data-testid="startup-banner-enabled"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Content */}
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm space-y-4">
          <div className="flex items-center gap-2 text-gray-900 font-medium">
            <Smile className="w-4 h-4 text-yellow-600" />
            Inhalt
          </div>
          <div>
            <Label className="text-gray-600">Emoji / Icon (unicode)</Label>
            <Input
              value={banner.emoji}
              onChange={(e) => update('emoji', e.target.value)}
              maxLength={4}
              className="w-32 text-2xl text-center bg-gray-50 border-gray-200 text-gray-900"
              data-testid="startup-banner-emoji"
            />
            <p className="text-xs text-gray-500 mt-1">z.B. 🎉 ⭐ 🎅 🌸 💪</p>
          </div>
          <div>
            <Label className="text-gray-600">Titel</Label>
            <Input
              value={banner.title}
              onChange={(e) => update('title', e.target.value)}
              maxLength={80}
              placeholder="Frohes neues Jahr!"
              className="bg-gray-50 border-gray-200 text-gray-900"
              data-testid="startup-banner-title"
            />
          </div>
          <div>
            <Label className="text-gray-600">Nachricht</Label>
            <Textarea
              value={banner.message}
              onChange={(e) => update('message', e.target.value)}
              rows={4}
              maxLength={500}
              placeholder="Wir wünschen dir ein erfolgreiches, gesundes Trainingsjahr 2027!"
              className="bg-gray-50 border-gray-200 text-gray-900"
              data-testid="startup-banner-message"
            />
          </div>
          <div>
            <Label className="text-gray-600">Bild-URL (optional)</Label>
            <Input
              value={banner.image_url || ''}
              onChange={(e) => update('image_url', e.target.value)}
              placeholder="https://…/banner.jpg"
              className="bg-gray-50 border-gray-200 text-gray-900"
              data-testid="startup-banner-image"
            />
          </div>
        </div>

        {/* Behavior + Link */}
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm space-y-4">
          <div className="flex items-center gap-2 text-gray-900 font-medium">
            <Clock className="w-4 h-4 text-cyan-400" />
            Verhalten
          </div>
          <div>
            <Label className="text-gray-600">Anzeigedauer (Sekunden)</Label>
            <div className="flex items-center gap-3 mt-1">
              <input
                type="range"
                min="2"
                max="10"
                value={banner.duration_seconds}
                onChange={(e) => update('duration_seconds', Number(e.target.value))}
                className="flex-1"
                data-testid="startup-banner-duration"
              />
              <span className="text-gray-900 font-semibold w-10 text-center">{banner.duration_seconds}s</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Banner wird nach dieser Zeit automatisch geschlossen (oder sofort beim Skip-Tap).
            </p>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-300 rounded-lg">
            <div>
              <p className="text-gray-900 font-medium">Überspringbar (X-Button)</p>
              <p className="text-xs text-gray-500">Nutzer kann Banner vorzeitig schliessen</p>
            </div>
            <Switch
              checked={!!banner.skippable}
              onCheckedChange={(v) => update('skippable', v)}
              data-testid="startup-banner-skippable"
            />
          </div>

          <div className="pt-3 border-t border-gray-200">
            <div className="flex items-center gap-2 text-gray-900 font-medium mb-3">
              <LinkIcon className="w-4 h-4 text-purple-600" />
              Aktion (optional)
            </div>
            <Label className="text-gray-600">Link-URL</Label>
            <Input
              value={banner.link_url || ''}
              onChange={(e) => update('link_url', e.target.value)}
              placeholder="/shop  oder  https://…"
              className="bg-gray-50 border-gray-200 text-gray-900"
              data-testid="startup-banner-link-url"
            />
            <p className="text-xs text-gray-500 mt-1">
              Interne App-Route (z.B. <code>/shop</code>) oder externe URL (öffnet im Browser).
            </p>
            <Label className="text-gray-600 mt-3 block">Button-Text</Label>
            <Input
              value={banner.link_label || ''}
              onChange={(e) => update('link_label', e.target.value)}
              placeholder="Jetzt ansehen"
              className="bg-gray-50 border-gray-200 text-gray-900"
              data-testid="startup-banner-link-label"
            />
          </div>
        </div>
      </div>

      {/* Schedule */}
      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm space-y-4">
        <div className="flex items-center gap-2 text-gray-900 font-medium">
          <Calendar className="w-4 h-4 text-green-600" />
          Zeitplan
        </div>
        <div>
          <Label className="text-gray-600">Modus</Label>
          <Select
            value={banner.schedule_mode}
            onValueChange={(v) => update('schedule_mode', v)}
          >
            <SelectTrigger
              className="bg-gray-50 border-gray-200 text-gray-900"
              data-testid="startup-banner-schedule-mode"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="always">Immer aktiv (solange „Aktiv" eingeschaltet ist)</SelectItem>
              <SelectItem value="scheduled">Geplant (Start/End-Datum)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {banner.schedule_mode === 'scheduled' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-600">Start (lokale Zeit)</Label>
              <Input
                type="datetime-local"
                value={toInputDateTime(banner.start_datetime)}
                onChange={(e) => update('start_datetime', fromInputDateTime(e.target.value))}
                className="bg-gray-50 border-gray-200 text-gray-900"
                data-testid="startup-banner-start-date"
              />
            </div>
            <div>
              <Label className="text-gray-600">Ende (lokale Zeit)</Label>
              <Input
                type="datetime-local"
                value={toInputDateTime(banner.end_datetime)}
                onChange={(e) => update('end_datetime', fromInputDateTime(e.target.value))}
                className="bg-gray-50 border-gray-200 text-gray-900"
                data-testid="startup-banner-end-date"
              />
            </div>
          </div>
        )}

        <div>
          <Label className="text-gray-600">Kampagnen-ID (optional)</Label>
          <Input
            value={banner.campaign_id || ''}
            onChange={(e) => update('campaign_id', e.target.value)}
            placeholder="NEUJAHR_2027"
            className="bg-gray-50 border-gray-200 text-gray-900"
            data-testid="startup-banner-campaign-id"
          />
          <p className="text-xs text-gray-500 mt-1">
            Für spätere Tracking-Statistik (optional).
          </p>
        </div>
      </div>

      {/* Colors + Preview */}
      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
        <div className="flex items-center gap-2 text-gray-900 font-medium mb-4">
          <Palette className="w-4 h-4 text-yellow-600" />
          Farben
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-gray-600">Hintergrund</Label>
            <div className="flex gap-2 items-center mt-1">
              <Input
                type="color"
                value={banner.background_color}
                onChange={(e) => update('background_color', e.target.value)}
                className="w-20 h-10 bg-gray-50 border-gray-200"
                data-testid="startup-banner-bg-color"
              />
              <Input
                value={banner.background_color}
                onChange={(e) => update('background_color', e.target.value)}
                className="flex-1 bg-gray-50 border-gray-200 text-gray-900"
              />
            </div>
          </div>
          <div>
            <Label className="text-gray-600">Akzentfarbe (Button)</Label>
            <div className="flex gap-2 items-center mt-1">
              <Input
                type="color"
                value={banner.primary_color}
                onChange={(e) => update('primary_color', e.target.value)}
                className="w-20 h-10 bg-gray-50 border-gray-200"
                data-testid="startup-banner-primary-color"
              />
              <Input
                value={banner.primary_color}
                onChange={(e) => update('primary_color', e.target.value)}
                className="flex-1 bg-gray-50 border-gray-200 text-gray-900"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-3 justify-end">
        <Button
          variant="outline"
          onClick={() => setShowPreview(true)}
          data-testid="startup-banner-preview-btn"
        >
          <Eye className="w-4 h-4 mr-2" />
          Vorschau
        </Button>
        <Button
          onClick={save}
          disabled={saving}
          className="bg-yellow-500 text-black hover:bg-yellow-400"
          data-testid="startup-banner-save-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Speichern…' : 'Banner speichern'}
        </Button>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.8)' }}
          onClick={() => setShowPreview(false)}
          data-testid="startup-banner-preview-modal"
        >
          <div
            className="max-w-sm w-full rounded-3xl overflow-hidden shadow-2xl relative"
            style={{ background: banner.background_color, color: '#ffffff' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Phone-frame preview */}
            <div className="p-8 text-center space-y-6">
              {banner.skippable && (
                <button
                  onClick={() => setShowPreview(false)}
                  className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 text-white/70 hover:bg-white/20 flex items-center justify-center"
                >
                  ✕
                </button>
              )}
              {banner.image_url ? (
                <img src={banner.image_url} alt="" className="w-full h-40 object-cover rounded-2xl" />
              ) : (
                <div className="text-7xl">{banner.emoji || '🎉'}</div>
              )}
              <div>
                <h2 className="text-2xl font-bold mb-2">{banner.title || 'Titel'}</h2>
                <p className="text-white/80 whitespace-pre-wrap">{banner.message || 'Nachricht'}</p>
              </div>
              {banner.link_url && (
                <button
                  className="w-full py-3 rounded-xl font-semibold text-black"
                  style={{ background: banner.primary_color }}
                >
                  {banner.link_label || 'Jetzt ansehen'}
                </button>
              )}
              <p className="text-xs text-gray-900/40">
                Wird nach {banner.duration_seconds}s automatisch geschlossen
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StartupBannerTab;
